import { useCallback, useEffect, useState } from "react";
import { getFavourites, setFavourite as saveFavourite } from "../utils/itemSearchApi";

// Loads this store's favourite items and toggles them.
// The flag lives on the product row (catalogue_product.is_favourite), so a
// favourite belongs to the store that owns the row.
export default function useFavourites(customerId) {
  const [favourites, setFavourites] = useState([]);
  const [ids, setIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    (signal) => {
      if (!customerId) return;
      setLoading(true);
      getFavourites(customerId, { signal })
        .then((items) => {
          setFavourites(items);
          setIds(new Set(items.map((item) => item.id)));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    },
    [customerId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const isFavourite = useCallback((item) => ids.has(item?.id), [ids]);

  // Update the heart straight away, then save. Reload on failure so the UI
  // never claims something was saved when it wasn't.
  const toggleFavourite = useCallback(
    async (item) => {
      if (!customerId || !item?.id) return;
      const next = !ids.has(item.id);

      setIds((prev) => {
        const updated = new Set(prev);
        if (next) updated.add(item.id);
        else updated.delete(item.id);
        return updated;
      });
      setFavourites((prev) =>
        next ? [...prev, item] : prev.filter((fav) => fav.id !== item.id)
      );

      try {
        await saveFavourite(customerId, item.id, next);
      } catch {
        load();
      }
    },
    [customerId, ids, load]
  );

  return { favourites, loading, isFavourite, toggleFavourite, reloadFavourites: load };
}
